import { getOrganizationReports } from "@/api/endpoints/organization";
import Layout from "@/containers/Layout";
import useSearchParams from "@/hooks/useSearchParams";
import { Organization } from "@/types/Organization";
import { ReportListItem } from "@/types/Report";
import AllEarsTitle from "@components/AllEarsTitle";
import Error from "@components/ui/error";
import { Skeleton } from "@components/ui/skeleton";
import { useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useTableColumns } from "./lib";
import DeleteReportDialog from "./partials/DeleteReportDialog";
import Header from "./partials/Header";
import { ReportsTable } from "./partials/ReportsTable";
import ReportTemplates from "./partials/ReportTemplates";
import AccessControl from "@components/AccessControl";

export default function OverviewView() {
  const { t } = useLingui();

  // URL state
  const { orgUuid } = useParams<{ orgUuid: Organization["uuid"] }>();

  // URL state
  const [{ deletingReportUuid }, setSearchParams] = useSearchParams<{
    deletingReportUuid?: ReportListItem["uuid"];
  }>();

  // Server state
  const {
    data: reportsData,
    error: reportsError,
    isLoading: isReportsLoading,
  } = useQuery({
    queryKey: [orgUuid, "reports"],
    queryFn: getOrganizationReports,
  });

  // Helpers
  const tableColumns = useTableColumns({
    orgUuid,
    onDelete: (reportUuid) =>
      setSearchParams({ deletingReportUuid: reportUuid }),
  });

  return (
    <>
      <AllEarsTitle title={t`Rapporter`} />

      <Layout>
        <Header orgUuid={orgUuid} />

        <div className="w-full p-4 lg:p-6 max-w-7xl flex flex-col gap-8">
          <AccessControl>
            <ReportTemplates />
          </AccessControl>

          {reportsData ? (
            reportsData.ok ? (
              <ReportsTable columns={tableColumns} data={reportsData.data} />
            ) : (
              <Error
                heading={t`Åh nej!`}
                description={t`Något gick snett med att ladda rapporterna. Vänligen försök igen senare.`}
              />
            )
          ) : isReportsLoading ? (
            <Skeleton className="h-80" />
          ) : reportsError ? (
            <Error
              error={reportsError}
              heading={t`Åh nej!`}
              description={t`Något gick snett med att ladda rapporterna. Vänligen försök igen senare.`}
            />
          ) : null}
        </div>
      </Layout>

      <DeleteReportDialog
        orgUuid={orgUuid}
        reportUuid={deletingReportUuid}
        onClose={() => {
          setSearchParams({ deletingReportUuid: undefined });
        }}
      />
    </>
  );
}
